import React, { useEffect, useState } from 'react';
import {
  Typography,
  Breadcrumb,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const InvoicesPayments = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const API_BASE_URL = '/api/invoices';

  const columns = [
    { title: 'Invoice #', dataIndex: 'invoice_no', key: 'invoice_no', width: 140 },
    { title: 'Entity Type', dataIndex: 'entity_type', key: 'entity_type', width: 120 },
    { title: 'Entity ID', dataIndex: 'entity_id', key: 'entity_id', width: 100 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, render: (v) => (v != null ? `$${Number(v).toLocaleString()}` : '—') },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => (
        <Tag color={s === 'paid' ? 'green' : s === 'due' ? 'orange' : s === 'overdue' ? 'red' : 'default'}>{s || '—'}</Tag>
      )
    },
    { title: 'Issued', dataIndex: 'issued_at', key: 'issued_at', width: 160 },
    { title: 'Due', dataIndex: 'due_at', key: 'due_at', width: 160 },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEdit(record)}>Edit</Button>
          <Popconfirm
            title="Delete this invoice?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(record.id)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const fetchInvoices = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize, offset: (page - 1) * pageSize });
      const { data: res } = await axios.get(`${API_BASE_URL}?${params.toString()}`);
      setData(res.items || []);
      setPagination({ current: page, pageSize, total: res.count ?? (res.items?.length || 0) });
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(1, pagination.pageSize); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTableChange = (pag) => {
    fetchInvoices(pag.current, pag.pageSize);
  };

  const authHeader = () => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      invoice_no: record.invoice_no,
      entity_type: record.entity_type,
      entity_id: record.entity_id,
      amount: record.amount,
      status: record.status,
      issued_at: record.issued_at ? dayjs(record.issued_at) : null,
      due_at: record.due_at ? dayjs(record.due_at) : null,
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/${id}`, { headers: authHeader() });
      message.success('Invoice deleted');
      fetchInvoices(pagination.current, pagination.pageSize);
    } catch (e) {
      message.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        invoice_no: values.invoice_no ?? null,
        entity_type: values.entity_type ?? null,
        entity_id: values.entity_id ?? null,
        amount: values.amount ?? null,
        status: values.status ?? null,
        issued_at: values.issued_at?.format ? values.issued_at.format('YYYY-MM-DD') : values.issued_at || null,
        due_at: values.due_at?.format ? values.due_at.format('YYYY-MM-DD') : values.due_at || null,
      };

      if (editing?.id) {
        await axios.put(`${API_BASE_URL}/${editing.id}`, payload, { headers: authHeader() });
        message.success('Invoice updated');
      } else {
        await axios.post(`${API_BASE_URL}`, payload, { headers: authHeader() });
        message.success('Invoice created');
      }
      setOpen(false);
      setEditing(null);
      fetchInvoices(pagination.current, pagination.pageSize);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Billing' },
        { title: 'Invoices & Payments' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Invoices & Payments</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>Create Invoice</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage invoices for agencies and clients. Create, edit, and delete invoices.
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onChange={onTableChange}
        />
      </Card>

      <Modal
        open={open}
        title={editing ? 'Edit Invoice' : 'Create Invoice'}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={onSubmit}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Invoice #" name="invoice_no" rules={[{ required: true, message: 'Enter invoice number' }]}>
            <Input placeholder="e.g., INV-1234" />
          </Form.Item>
          <Form.Item label="Entity Type" name="entity_type" rules={[{ required: true }]}>
            <Select placeholder="Select entity type">
              <Option value="agency">Agency</Option>
              <Option value="client">Client</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Entity ID" name="entity_id" rules={[{ required: true, type: 'number', min: 1 }]}>
            <InputNumber style={{ width: '100%' }} placeholder="ID of agency or client" />
          </Form.Item>
          <Form.Item label="Amount" name="amount" rules={[{ required: true, type: 'number', min: 0 }]}> 
            <InputNumber style={{ width: '100%' }} placeholder="e.g., 149" />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true }]}> 
            <Select placeholder="Select status">
              <Option value="paid">Paid</Option>
              <Option value="due">Due</Option>
              <Option value="overdue">Overdue</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Issued At" name="issued_at">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item label="Due At" name="due_at">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoicesPayments;
